import { AgGridAngular } from "@ag-grid-community/angular";
import { GridOptions } from "@ag-grid-community/core";
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from "@angular/core";
import { DspCreative, DspStats } from "@front/m19-api-client";
import { IButtonDirective, ITooltipDirective, MODAL_DATA, ModalRef } from "@front/m19-ui";
import { sumDspStats } from "@front/m19-utils";
import { TranslocoDirective } from "@jsverse/transloco";
import { restoreDefaultColumns } from "@m19-board/grid-config/grid-config";
import { ExportButtonComponent } from "@m19-board/shared/ui/export-buttons/export-button.component";
import { DspCreativeStats, DspGridColumnsService, dspGridOptions } from "./dsp-grid-columns.service";

@Component({
  selector: "app-dsp-stats-creative-details-modal",
  template: `
    <div *transloco="let t">
      <div class="mb-2 flex w-full items-center justify-end gap-1">
        <button
          iButton
          variant="secondary"
          size="md"
          [iTooltip]="t('common.restore_default_columns')"
          (click)="restoreDefaultColumns()"
        >
          {{ t("common.restore_columns") }}
        </button>
        <app-export-button [tooltipValue]="t('common.export_as_csv')" (export)="exportGridCsv()" />
      </div>
      <div class="ag-theme-quartz grid">
        <ag-grid-angular
          #grid
          [rowData]="rowData"
          [gridOptions]="gridOptions"
          [animateRows]="true"
          class="h-[40vh] w-full"
        >
        </ag-grid-angular>
      </div>
    </div>
  `,
  imports: [IButtonDirective, ITooltipDirective, TranslocoDirective, ExportButtonComponent, AgGridAngular],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspStatsCreativeDetailsModalComponent implements OnInit {
  private readonly dspColumns = inject(DspGridColumnsService);
  private readonly modalData = inject<DspStatsCreativeDetailsModalData>(MODAL_DATA);
  modalRef = inject(ModalRef);

  rowData: DspCreativeStats[] = [];
  readonly gridOptions: GridOptions<DspCreativeStats> = {
    ...dspGridOptions<DspCreativeStats>({ gridKey: "dsp_creative_stats", autoSizeDefault: true }),
    columnDefs: [
      this.dspColumns.creativeNameColumn<DspCreativeStats>(),
      this.dspColumns.creativeTypeColumn<DspCreativeStats>({ colId: "type", value: (data) => data.type }),
      ...this.dspColumns.metricsColumns<DspCreativeStats>(),
      this.dspColumns.currencyColumn<DspCreativeStats>(),
    ],
  };

  @ViewChild("grid") grid!: AgGridAngular;

  ngOnInit(): void {
    const dataPerCrea = new Map<string, DspCreativeStats>();
    for (const stat of this.modalData.creativeData) {
      if (!stat.creativeId) {
        continue;
      }
      if (!dataPerCrea.has(stat.creativeId)) {
        const creative = this.modalData.creatives.get(stat.creativeId);
        if (!creative) {
          continue;
        }
        dataPerCrea.set(stat.creativeId, {
          ...stat,
          dspCreativeName: creative.name,
          type: creative.adCreativeFormatProperties?.adCreativeFormatType,
          lineItemStats: [],
        });
      } else {
        const existing = dataPerCrea.get(stat.creativeId);
        if (existing) {
          dataPerCrea.set(stat.creativeId, {
            ...existing,
            ...sumDspStats(existing, stat),
          });
        }
      }
    }
    this.rowData = Array.from(dataPerCrea.values());
  }

  close() {
    this.modalRef.close();
  }

  restoreDefaultColumns() {
    restoreDefaultColumns("dsp_adgroup_stats", this.grid.api);
  }

  exportGridCsv() {
    this.dspColumns.exportCsv(this.grid.api, {
      fileName: "dsp_creative_stats",
      columnHeaders: { dspCreativeName: "dsp-stats.creative", type: "dsp-stats.creativeType" },
    });
  }
}

export interface DspStatsCreativeDetailsModalData {
  creativeData: DspStats[];
  creatives: Map<string, DspCreative>;
}
